/**
 * Generic Form Component
 * ======================
 * Reusable Alpine.js component for form handling with file uploads.
 *
 * Usage in Twig:
 * <div x-data="form()">
 *   <form @submit.prevent="submitForm()">
 *     ...
 *   </form>
 * </div>
 *
 * When the server returns a specific validation message, it is assigned to
 * `errorMessage`. Otherwise `errorMessage` stays empty — the template is
 * expected to render a fallback via `x-text="errorMessage || $el.dataset.default"`.
 */

const STATE_IDLE = 'idle'
const STATE_LOADING = 'loading'
const STATE_SUCCESS = 'success'
const STATE_ERROR = 'error'

export default () => ({
  // State
  state: STATE_IDLE,
  errorMessage: '',
  files: {}, // Grouped files: { attachments: [], documents: [] }

  // State helpers
  isIdle() {
    return this.state === STATE_IDLE
  },

  isLoading() {
    return this.state === STATE_LOADING
  },

  isSuccess() {
    return this.state === STATE_SUCCESS
  },

  isError() {
    return this.state === STATE_ERROR
  },

  /**
   * Initialize file group
   */
  initFileGroup(group) {
    if (!this.files[group]) {
      this.files[group] = []
    }
  },

  /**
   * Add files to a group
   */
  addFiles(group, event) {
    this.initFileGroup(group)
    const newFiles = Array.from(event.target.files)
    this.files[group].push(...newFiles)
    // Reset input so same file can be selected again
    event.target.value = ''
  },

  /**
   * Remove file from a group
   */
  removeFile(group, index) {
    if (this.files[group]) {
      this.files[group].splice(index, 1)
    }
  },

  /**
   * Clear all files in a group
   */
  clearFiles(group) {
    if (this.files[group]) {
      this.files[group] = []
    }
  },

  /**
   * Validate files in a group
   */
  validateFiles(group, maxSizeBytes = 10 * 1024 * 1024) {
    if (!this.files[group]) return true

    for (const file of this.files[group]) {
      if (file.size > maxSizeBytes) {
        return false
      }
    }
    return true
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  },

  /**
   * Submit the form
   */
  async submitForm() {
    // Prevent double submission (but allow retry after error)
    if (!this.isIdle() && !this.isError()) return

    this.state = STATE_LOADING
    this.errorMessage = ''

    try {
      // Get form reference
      const form = this.$refs.form || this.$el.querySelector('form')
      if (!form) {
        throw new Error('Form element not found')
      }

      // Build FormData
      const formData = new FormData(form)

      // Append files from our managed groups
      for (const [group, files] of Object.entries(this.files)) {
        // Remove any existing file inputs for this group (they're managed separately)
        formData.delete(`${group}[]`)

        // Add our managed files
        for (const file of files) {
          formData.append(`${group}[]`, file)
        }
      }

      // Submit via fetch
      const response = await fetch('', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || '')
      }

      // Success - redirect if URL provided
      this.state = STATE_SUCCESS

      const redirectUrl = form.querySelector('input[name="redirectUrl"]')?.value
      if (redirectUrl) {
        window.location.href = redirectUrl
      }

    } catch (error) {
      console.error('Form submission error:', error)
      this.state = STATE_ERROR
      this.errorMessage = error.message
    }
  },

  /**
   * Reset form state
   */
  reset() {
    this.state = STATE_IDLE
    this.errorMessage = ''
    this.files = {}
  }
})
